import React, { useState } from "react";
import { AlertCircle, Clock, Tag } from "lucide-react";

const InlineError = ({ msg }) =>
  msg ? (
    <p className="flex items-center gap-1.5 mt-1 text-xs text-red-400">
      <AlertCircle size={11} />
      {msg}
    </p>
  ) : null;

const AIStep4_Save = ({
  jobRole,
  minExp,
  maxExp,
  topics,
  questionsPerTopic,
  questions,
  setName,
  setSetName,
  mcqTimelimit,
  setMcqTimelimit,
  onSave,
  onBack,
  isSaving,
}) => {
  const [nameError, setNameError] = useState("");

  const totalQuestions = questions.length;
  const topicSummary = topics
    .map((t) => `${t} (${questionsPerTopic[t] || 0})`)
    .join(", ");

  const handleSave = () => {
    if (!setName.trim()) {
      setNameError("Set name is required");
      return;
    }
    setNameError("");
    onSave();
  };

  return (
    <div className="w-full flex flex-col lg:flex-row gap-8">
      {/* Left: Save settings */}
      <div className="flex-1 space-y-5">
        {/* Set name & time limit */}
        <div className="rounded-2xl border border-white/10 bg-bgSecondary dark:glass-effect p-6 space-y-5">
          <h3 className="text-base font-semibold dark:text-white text-textPrimary">Save your MCQ Set</h3>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium dark:text-gray-300 text-textSecondary">
              <Tag size={14} />
              Set Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g. React Fundamentals Q1 2025"
              value={setName}
              onChange={(e) => { setSetName(e.target.value); setNameError(""); }}
              className="gradient-ring dark:glass-effect bg-bgPrimary dark:!text-white !text-textPrimary border border-white/10 rounded-xl w-full px-4 py-3 text-sm outline-none transition-colors"
            />
            <InlineError msg={nameError} />
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium dark:text-gray-300 text-textSecondary">
              <Clock size={14} />
              Time Limit (minutes)
            </label>
            <input
              type="number"
              min="0"
              placeholder="0 = no limit"
              value={mcqTimelimit}
              onChange={(e) => setMcqTimelimit(e.target.value)}
              className="gradient-ring dark:glass-effect bg-bgPrimary dark:!text-white !text-textPrimary border border-white/10 rounded-xl w-40 px-4 py-3 text-sm outline-none transition-colors"
            />
            <p className="text-xs dark:text-gray-500 text-textSecondary">Leave 0 for no time limit.</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="border-btn dark:border-btn-dark flex-1 px-4 py-2.5 rounded-full text-sm font-medium transition-all"
          >
            ← Back to Review
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="button flex-1 flex items-center justify-center gap-2"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>Saving...</span>
              </>
            ) : (
              <span>Save MCQ Set ✓</span>
            )}
          </button>
        </div>
      </div>

      {/* Right: Summary panel */}
      <div className="lg:w-80 rounded-2xl border border-white/10 bg-bgSecondary dark:glass-effect p-6 space-y-4 self-start">
        <h3 className="text-sm font-semibold dark:text-white text-textPrimary">Summary</h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between items-start">
            <p className="text-xs dark:text-gray-500 text-textSecondary">Role</p>
            <p className="font-medium dark:text-gray-200 text-textPrimary text-right max-w-[60%]">{jobRole || "—"}</p>
          </div>
          <div className="flex justify-between items-start">
            <p className="text-xs dark:text-gray-500 text-textSecondary">Experience</p>
            <p className="font-medium dark:text-gray-200 text-textPrimary">{minExp}–{maxExp} yrs</p>
          </div>
          <div className="flex justify-between items-start">
            <p className="text-xs dark:text-gray-500 text-textSecondary">Total Questions</p>
            <p className="font-semibold text-textHeading">{totalQuestions}</p>
          </div>
          <div className="pt-2 border-t border-white/10 space-y-2">
            <p className="text-xs dark:text-gray-500 text-textSecondary">Topics</p>
            <div className="flex flex-wrap gap-1.5">
              {topics.map((t) => (
                <span key={t} className="gradient-ring is-active px-2 py-0.5 rounded-full text-[10px] font-medium border border-transparent bg-gradient-to-r from-pink-500/20 to-blue-500/20 dark:text-gray-300 text-textSecondary">
                  {t} ({questionsPerTopic[t] || 0})
                </span>
              ))}
              {topics.length === 0 && <p className="text-xs dark:text-gray-500 text-textSecondary">—</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIStep4_Save;
